/**
 * Featured Products section.
 * Handles collection tab switching, carousel scrolling, quick-add to cart,
 * color swatch interaction, and mobile dropdown.
 */
class FeaturedProducts extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.grid = this.querySelector("[data-product-grid]");
    this.tabs = this.querySelectorAll("[data-tab]");
    this.prevBtns = this.querySelectorAll("[data-scroll-prev]");
    this.nextBtns = this.querySelectorAll("[data-scroll-next]");
    this.mobileToggle = this.querySelector("[data-mobile-toggle]");
    this.mobileDropdown = this.querySelector("[data-mobile-dropdown]");
    this.mobileLabel = this.querySelector("[data-mobile-active-label]");

    this.activeIndex = 0;
    this.cache = new Map();
    this.isLoading = false;

    this.#seedCollectionCache();

    // Cache initial grid HTML
    const firstTab = this.querySelector("[data-tab][data-collection]");
    if (firstTab) {
      this.cache.set(firstTab.dataset.collection, this.grid.innerHTML);
    }

    this.#bindTabs();
    this.#bindArrows();
    this.#bindMobileDropdown();
  }

  // ── Tab switching ──

  #bindTabs() {
    this.tabs.forEach((tab) => {
      tab.addEventListener("click", () => {
        const index = parseInt(tab.dataset.tab);
        if (index === this.activeIndex && !this.mobileDropdown?.contains(tab))
          return;
        this.#switchTab(index, tab.dataset.collection, tab.textContent.trim());

        // Close mobile dropdown
        this.mobileDropdown?.classList.add("hidden");
      });
    });
  }

  #seedCollectionCache() {
    const templates = this.querySelectorAll("[data-collection-template]");
    templates.forEach((template) => {
      const handle = template.dataset.collectionTemplate;
      if (!handle) return;
      this.cache.set(handle, template.innerHTML);
    });
  }

  async #switchTab(index, handle, tabLabel = "") {
    if (this.isLoading) return;
    this.activeIndex = index;

    // Update active tab styles
    this.tabs.forEach((t) => {
      const isActive = parseInt(t.dataset.tab) === index;
      t.classList.toggle("is-active", isActive);
    });

    // Update mobile label
    if (this.mobileLabel && tabLabel) {
      this.mobileLabel.textContent = tabLabel;
    }

    // Load products
    if (this.cache.has(handle)) {
      this.grid.innerHTML = this.cache.get(handle);
      this.grid.scrollLeft = 0;
      // Card interactions handled by delegated product-card.js
      return;
    }

    this.isLoading = true;
    this.grid.style.opacity = "0.5";

    try {
      const res = await fetch(
        `/collections/${handle}?section_id=${this.sectionId}`,
      );
      const html = await res.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      const newGrid = doc.querySelector("[data-product-grid]");

      if (newGrid) {
        this.cache.set(handle, newGrid.innerHTML);
        this.grid.innerHTML = newGrid.innerHTML;
        this.grid.scrollLeft = 0;
        // Card interactions handled by delegated product-card.js
      }
    } catch (e) {
      console.error("Failed to load collection:", e);
    } finally {
      this.grid.style.opacity = "1";
      this.isLoading = false;
    }
  }

  // ── Carousel scrolling ──

  #bindArrows() {
    const scrollAmount = () => {
      const card = this.grid.querySelector("[data-product-card]");
      if (!card) return 300;
      const gap = parseFloat(getComputedStyle(this.grid).gap) || 20;
      return card.offsetWidth + gap;
    };

    this.prevBtns.forEach((btn) => {
      btn.addEventListener("click", () => {
        this.grid.scrollBy({ left: -scrollAmount(), behavior: "smooth" });
      });
    });

    this.nextBtns.forEach((btn) => {
      btn.addEventListener("click", () => {
        this.grid.scrollBy({ left: scrollAmount(), behavior: "smooth" });
      });
    });
  }

  // ── Mobile dropdown ──

  #bindMobileDropdown() {
    if (!this.mobileToggle || !this.mobileDropdown) return;

    this.mobileToggle.addEventListener("click", () => {
      const isOpen = !this.mobileDropdown.classList.contains("hidden");
      this.mobileDropdown.classList.toggle("hidden");
      const chevron = this.mobileToggle.querySelector("svg");
      if (chevron) {
        chevron.style.transform = isOpen ? "" : "rotate(180deg)";
      }
    });

    // Close on outside click
    document.addEventListener("click", (e) => {
      const wrapper = this.querySelector("[data-mobile-tab-wrapper]");
      if (wrapper && !wrapper.contains(e.target)) {
        this.mobileDropdown.classList.add("hidden");
        const chevron = this.mobileToggle.querySelector("svg");
        if (chevron) chevron.style.transform = "";
      }
    });
  }
}

customElements.define("featured-products", FeaturedProducts);

export { FeaturedProducts };
